/**
 * What a schedule can be set to: the frequencies, the pickers each one shows,
 * their choices and their defaults.
 *
 * The Schedule block's sub-blocks and the Schedule popover above the canvas
 * both draw their fields from here, so they cannot offer different choices or
 * start from different values. Constants only: the block registry imports this
 * file, and the parsing in `model.ts` has no business in that bundle.
 */

export type ScheduleType = 'minutes' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom'

/** The block's sub-block ids, which are also the settings' keys. */
export const SCHEDULE_FIELDS = [
  'scheduleType',
  'minutesInterval',
  'hourlyMinute',
  'dailyTime',
  'weeklyDay',
  'weeklyDayTime',
  'monthlyDay',
  'monthlyTime',
  'cronExpression',
  'timezone',
] as const

export type ScheduleField = (typeof SCHEDULE_FIELDS)[number]

/** Every value as the block stores it: text, as a picker writes it. */
export type ScheduleSettings = Record<ScheduleField, string>

export const SCHEDULE_TYPE_OPTIONS: { label: string; id: ScheduleType }[] = [
  { label: 'Every X Minutes', id: 'minutes' },
  { label: 'Hourly', id: 'hourly' },
  { label: 'Daily', id: 'daily' },
  { label: 'Weekly', id: 'weekly' },
  { label: 'Monthly', id: 'monthly' },
  { label: 'Custom (Cron)', id: 'custom' },
]

/**
 * Only the steps that divide the hour. A cron step restarts at the top of every
 * hour, so `*\/45` fires at :00 and :45 — gaps of 45 and 15 minutes under a
 * label that promised 45 — and `*\/90` is not an interval at all.
 */
export const MINUTE_INTERVALS = ['1', '2', '3', '4', '5', '6', '10', '12', '15', '20', '30']

export const WEEKDAY_OPTIONS = [
  { label: 'Monday', id: 'MON' },
  { label: 'Tuesday', id: 'TUE' },
  { label: 'Wednesday', id: 'WED' },
  { label: 'Thursday', id: 'THU' },
  { label: 'Friday', id: 'FRI' },
  { label: 'Saturday', id: 'SAT' },
  { label: 'Sunday', id: 'SUN' },
]

/** The field labels, as the block titles them and the popover labels them. */
export const SCHEDULE_FIELD_LABELS: Record<ScheduleField, string> = {
  scheduleType: 'Run frequency',
  minutesInterval: 'Interval (minutes)',
  hourlyMinute: 'Minute',
  dailyTime: 'Time',
  weeklyDay: 'Day of week',
  weeklyDayTime: 'Time',
  monthlyDay: 'Day of month',
  monthlyTime: 'Time',
  cronExpression: 'Cron expression',
  timezone: 'Timezone',
}

/**
 * Which frequencies show which field. A minute interval has no timezone: every
 * zone's offset is a whole number of the intervals above, so the runs land on
 * the same instants wherever the clock is read.
 */
export const SCHEDULE_FIELD_TYPES: Record<
  Exclude<ScheduleField, 'scheduleType'>,
  ScheduleType[]
> = {
  minutesInterval: ['minutes'],
  hourlyMinute: ['hourly'],
  dailyTime: ['daily'],
  weeklyDay: ['weekly'],
  weeklyDayTime: ['weekly'],
  monthlyDay: ['monthly'],
  monthlyTime: ['monthly'],
  cronExpression: ['custom'],
  timezone: ['hourly', 'daily', 'weekly', 'monthly', 'custom'],
}

export function scheduleFieldsFor(type: string): Exclude<ScheduleField, 'scheduleType'>[] {
  return (Object.keys(SCHEDULE_FIELD_TYPES) as Exclude<ScheduleField, 'scheduleType'>[]).filter(
    (field) => SCHEDULE_FIELD_TYPES[field].includes(type as ScheduleType)
  )
}

export const SCHEDULE_DEFAULTS: Omit<ScheduleSettings, 'timezone'> = {
  scheduleType: 'daily',
  minutesInterval: '15',
  hourlyMinute: '0',
  dailyTime: '09:00',
  weeklyDay: 'MON',
  weeklyDayTime: '09:00',
  monthlyDay: '1',
  monthlyTime: '09:00',
  cronExpression: '0 9 * * *',
}

export function defaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * The fields a dropdown renders: a dropdown with nothing stored shows its
 * default, so reading one as empty would describe a schedule the block is not
 * showing. A text or time field with nothing stored shows nothing, and is read
 * as nothing.
 */
const DROPDOWN_FIELDS: ScheduleField[] = [
  'scheduleType',
  'minutesInterval',
  'weeklyDay',
  'timezone',
]

export function readScheduleSettings(read: (field: ScheduleField) => unknown): ScheduleSettings {
  const settings = {} as ScheduleSettings
  for (const field of SCHEDULE_FIELDS) {
    const value = read(field)
    if (value === null || value === undefined || value === '') {
      settings[field] = DROPDOWN_FIELDS.includes(field)
        ? field === 'timezone'
          ? defaultTimezone()
          : SCHEDULE_DEFAULTS[field as Exclude<ScheduleField, 'timezone'>]
        : ''
    } else {
      settings[field] = String(value)
    }
  }
  return settings
}

export function defaultScheduleSettings(): ScheduleSettings {
  return { ...SCHEDULE_DEFAULTS, timezone: defaultTimezone() }
}

export const TIMEZONE_OPTIONS: { label: string; id: string }[] = [
  // UTC
  { label: 'UTC', id: 'UTC' },
  // Americas
  { label: 'US Pacific (UTC-8)', id: 'America/Los_Angeles' },
  { label: 'US Mountain (UTC-7)', id: 'America/Denver' },
  { label: 'US Central (UTC-6)', id: 'America/Chicago' },
  { label: 'US Eastern (UTC-5)', id: 'America/New_York' },
  { label: 'US Alaska (UTC-9)', id: 'America/Anchorage' },
  { label: 'US Hawaii (UTC-10)', id: 'Pacific/Honolulu' },
  { label: 'Canada Toronto (UTC-5)', id: 'America/Toronto' },
  { label: 'Canada Vancouver (UTC-8)', id: 'America/Vancouver' },
  { label: 'Mexico City (UTC-6)', id: 'America/Mexico_City' },
  { label: 'São Paulo (UTC-3)', id: 'America/Sao_Paulo' },
  { label: 'Buenos Aires (UTC-3)', id: 'America/Argentina/Buenos_Aires' },
  // Europe
  { label: 'London (UTC+0)', id: 'Europe/London' },
  { label: 'Paris (UTC+1)', id: 'Europe/Paris' },
  { label: 'Berlin (UTC+1)', id: 'Europe/Berlin' },
  { label: 'Amsterdam (UTC+1)', id: 'Europe/Amsterdam' },
  { label: 'Madrid (UTC+1)', id: 'Europe/Madrid' },
  { label: 'Rome (UTC+1)', id: 'Europe/Rome' },
  { label: 'Moscow (UTC+3)', id: 'Europe/Moscow' },
  // Middle East / Africa
  { label: 'Dubai (UTC+4)', id: 'Asia/Dubai' },
  { label: 'Tel Aviv (UTC+2)', id: 'Asia/Tel_Aviv' },
  { label: 'Cairo (UTC+2)', id: 'Africa/Cairo' },
  { label: 'Johannesburg (UTC+2)', id: 'Africa/Johannesburg' },
  // Asia
  { label: 'India (UTC+5:30)', id: 'Asia/Kolkata' },
  { label: 'Bangkok (UTC+7)', id: 'Asia/Bangkok' },
  { label: 'Jakarta (UTC+7)', id: 'Asia/Jakarta' },
  { label: 'Singapore (UTC+8)', id: 'Asia/Singapore' },
  { label: 'China (UTC+8)', id: 'Asia/Shanghai' },
  { label: 'Hong Kong (UTC+8)', id: 'Asia/Hong_Kong' },
  { label: 'Seoul (UTC+9)', id: 'Asia/Seoul' },
  { label: 'Tokyo (UTC+9)', id: 'Asia/Tokyo' },
  // Australia / Pacific
  { label: 'Perth (UTC+8)', id: 'Australia/Perth' },
  { label: 'Sydney (UTC+10)', id: 'Australia/Sydney' },
  { label: 'Melbourne (UTC+10)', id: 'Australia/Melbourne' },
  { label: 'Auckland (UTC+12)', id: 'Pacific/Auckland' },
]

/** The list, plus the zone a block already holds when the list does not name it. */
export function timezoneOptions(current: string): { label: string; id: string }[] {
  if (!current || TIMEZONE_OPTIONS.some((option) => option.id === current)) return TIMEZONE_OPTIONS
  return [{ label: current, id: current }, ...TIMEZONE_OPTIONS]
}
